import React, {memo, PureComponent, useEffect, useCallback, useState, useRef, Component } from 'react';
import MouseHelper from './MouseHelper.js';
import {useSticky} from './useSticky.js';
import {useScroll} from './useScroll.js';
import {onScale} from './onScale.js';
import ConditionedComponent from 'components/ConditionedComponent.js';
import {getHeight} from './getHeight.js';
import {
    setArrangementXScale,
    setArrangementCursorStep,
    setArrangementMouseX
} from "actions/index.js";
import {getTotalLength} from './getTotalLength.js';
import {getStepFromPosition} from './getStepFromPosition.js';
import { connect } from "react-redux";
import {getBarWidth} from './getBarWidth.js';
import {INFO_COLUMN_WIDTH} from './constants.js';

const MIN_X_SCALE = 0.1;
const SCROLL_BUFFER_LENGTH = 140;

const TimeRowComponent = (props) => {
    const ref = useRef(undefined);

    const {sticky} = useSticky({
        showTracks: props.showTracks,
        stickyContainer: ref,
        scrollContainer: props.yScrollContainer,
        isBottom: props.isBottom,
        bottomHeight: props.bottomHeight,
        showBottom: props.showBottom
    });

    const mouseDown = useRef(undefined);
    const dragStartY = useRef(undefined);
    const dragY = useRef(undefined);
    const dragX = useRef(undefined);
    const timeout = useRef(undefined);

    const onMouseDown = useCallback((e) =>{
        mouseDown.current = true;
        dragStartY.current = e.pageY;
        dragY.current = e.pageY;
        dragX.current = e.pageX;

        timeout.current = setInterval(() => {
            let diff = dragY.current - dragStartY.current;
            let _xScale = props.sequencerEngine.state.arrangement.xScale ;
            let xScale = _xScale + diff / 50.0;
            xScale = Math.max(MIN_X_SCALE, xScale);
            xScale = Math.min(5, xScale);

            if (xScale === _xScale) {
                return;
            }

            if (getBarWidth(16*props.totalLength, props.resolution, xScale) < window.innerWidth - 140) {
                // Dont let user zoom out beyond the width of the window
                return;
            }

            onScale({
                xScale,
                setArrangementXScale: props.setArrangementXScale,
                cursorStep: cursorStep(),
                resolution: props.resolution,
                x: dragX.current,
                arrangementContainer: props.arrangementContainer
            });
        }, 100);
    }, [props.resolution, props.totalLength]);

    const onMouseUp = (e) => {
        clearInterval(timeout.current);
        mouseDown.current = false;
        dragStartY.current = undefined;
    }

    const onMouseMove = (e) => {
        if (!mouseDown.current) {
            return;
        }

        dragY.current = e.pageY;
    };

    const cursorStep = () => {
        return props.sequencerEngine.state.arrangement.cursorStep;
    };

    
    let className = "time-row";
    if (props.constantHeight) {
        className += ' bottom';
    }

    let style = {};

    return (
        <div
          ref={ref}
          style={style}
          className={className}>
          <div className="time-top-background"></div>
          <div className="time-full-background"></div>
          <div
            onMouseLeave={onMouseUp}
            onMouseMove={onMouseMove}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            className="inner-time-row">
            <MouseHelper
              sequencerEngine={props.sequencerEngine}
              offset={-5}
              isSelected={true}/>
            <Bars  {...props}/>
          </div>
          {props.children}
        </div>
    );
}

const Bars = memo((props) => {
    const getEvents = useCallback(() => {
        let numBars = props.totalLength;
        let arr = []; 
        for (let i=0; i < numBars; i++) {
            arr.push({
                step: i * 16,
                duration: 32
            });
        }
        return arr;
    }, [props.totalLength]);

    const {visibleView} = useScroll({
        ref: props.arrangementContainer,
        xScale: props.xScale,
        resolution: props.resolution,
        events: getEvents(),
        sequencerEngine: props.sequencerEngine,
        eventsLength: props.totalLength,
    });

    let bars = getEvents().slice(
        visibleView.start, visibleView.end);

    let barWidth = getBarWidth(16, props.resolution, props.xScale);

    return [(
        <div className="bar-filler" style={{minWidth: visibleView.preWidth}}/>),
            bars.map(
                ({step, value}, i) =>
                    <BarComponent
                      xScale={props.xScale}
                      height={props.height}
                      key={step / 16.0}
                      barWidth={barWidth}
                      selectionStart={props.selectionStart}
                      selectionEnd={props.selectionEnd}
                      constantHeight={props.constantHeight}
                      sequencerEngine={props.sequencerEngine}
                      resolution={props.resolution}
                      number={step / 16.0}/>)
           ];
});

class BarComponent extends PureComponent {
    render() {
        let beat = this.props.number*(8 / this.props.resolution);
        let style = {
            minWidth: this.props.barWidth,
        };
        let beatStyle = {};
        if (this.props.xScale <= 0.8) {
            beatStyle['opacity'] = 0.5;
        }

        beatStyle['height'] = this.props.height;
        let firstStyle = {
            height: beatStyle.height
        };

        if (this.props.xScale <= 0.5 && this.props.number % 4 !== 0) {
            firstStyle['opacity'] = 0.4;
        }

        let beatClass = 'beat';
        if (this.props.xScale <= 0.5) {
            beatClass += ' hidden';
        }
        
        return (
            <div
              style={style}
              className="bar">
              <ConditionedComponent condition={this.props.xScale > 0.6 || beat % 4 === 0}>
                <span className="beat-number">
                  {1 + beat}
                </span>
              </ConditionedComponent>
              <div style={firstStyle} className="first-beat">
              </div>
              <div style={beatStyle} className={beatClass}>
              </div>
              <div style={beatStyle} className={beatClass}></div>
              <div style={beatStyle} className={beatClass}></div>
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch, props) => {
    return {
        setArrangementXScale: (xScale) => dispatch(
            setArrangementXScale(xScale)),
        setArrangementMouseX: (x) => dispatch(
            setArrangementMouseX(x)),
        setArrangementCursorStep: (x) => dispatch(
            setArrangementCursorStep(x)),
    };
};
 
const mapStateToProps = (state, props) => {
    let sub = props.sequencerEngine.currentSubSequence;
    return {
        numSubSequences: props.sequencerEngine.subSequences.length,
        xScale: state.arrangement.xScale,
        totalLength: state.arrangement.totalLength ,
        showTracks: state.arrangement.showTracks,
        bottomHeight: state.arrangement.bottomHeight,
        showBottom: state.arrangement.patternSelected && state.arrangement.showTracks[sub.id]
    };

};
 
export default connect(mapStateToProps, mapDispatchToProps)(TimeRowComponent);
